import { set } from '@ember/object';
import Service, { type Registry as Services, service } from '@ember/service';
import { isTesting, macroCondition } from '@embroider/macros';
import type { Dayjs } from 'dayjs';
import { task, timeout } from 'ember-concurrency';
import type { PaAutopilotSettings } from 'frontend-utils/models/hotel';
import type { Nullable } from 'frontend-utils/utils/nullable';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type {
  Carate,
  Carecommendation,
  Day,
  Occupancy,
  Override,
  PaPmsInfo,
  PaRecommendationReason,
  PaSubscriptionMetaInfo,
  Period,
  Pms,
  PredictedOccupancy,
  RecommendationStrategy,
  RestrictionRecommendation,
  RoomTypeRecommendation,
} from 'lumen/models';
import type { RoomOffsetHierarchySettings } from 'lumen/models/recommendation-strategy';
import type { Params as HotelPricingManagerParams } from 'lumen/routes/hotel/pricing-manager';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type {
  CarecommendationActionEntry,
  CarecommendationMock,
  PricingOverridePayload,
} from 'lumen/services/pricing-assistant-handle-recommendation-actions-service';
import {
  APIGATEWAY,
  COMPARETYPES,
} from 'lumen/utils/constants/general-constants';
import {
  type OpenRoomPricingFixedCategory,
  PA_REF_DAYS_AGO,
  RECOMMENDATION_ACTION_PRICE_TYPES,
  type RecommendationActionPriceTypesType,
  RECOMMENDATIONS_ACTION_ENDPOINT_ERROR_CODES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import getAPIErrorMessage, {
  isApiError,
} from 'lumen/utils/get-api-error-message';
import { getFlattenedHierarchy } from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';
import { mapQueryParamsToAPIParams } from 'lumen/utils/rates-request';
import type { PricePushMeta } from 'lumen/utils/roi-trophy/types';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

interface ActionConfig {
  action: Carecommendation['action'];
  carate: Carate['id'];
  current_rate: Carate['value'];
  factors: Carecommendation['factors'];
  from_date: string;
  overwritten_value: Carecommendation['overwrittenValue'];
  pms_roomtype: Carate['pmsRoomtype'];
  price_type?: RecommendationActionPriceTypesType;
  rate_code: Carate['rateCode'];
  recommendation_id: Carecommendation['id'];
  recommended_value: Carecommendation['recommendedValue'];
  subscription: number;
}

interface ReRequestDataAfterPushParams {
  endDate: string;
  fetchOverrides?: boolean;
  fromDate: string;
  hotelId: string;
}

type ActionConfigs = {
  action_configs: ActionConfig[];
  override?: PricingOverridePayload;
};

interface BulkPushResponse {
  action_configs: CarecommendationActionEntry[];
  meta?: PricePushMeta;
}

interface AutopilotExcludeResponse {
  pa_autopilot_settings: PaAutopilotSettings;
}

export interface RawRecommendationData {
  carate: string;
  current_discount: number | null;
  current_value: number;
  id: string;
  pms_roomtype: string;
  rate_code: string;
  recommended_discount: number | null;
  recommended_value: number;
}

export default class PricingAssistantDataRequestServiceService extends Service {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @service declare intl: Services['intl'];
  @service declare pricingAssistantPmsSs: Services['pricing-assistant-pms-ss'];
  @service
  declare pricingAssistantRecommendationActionPoller: Services['pricing-assistant-recommendation-action-poller'];
  @service('restrictions/action-poller')
  declare restrictionsActionPoller: Services['restrictions/action-poller'];
  @service('restrictions/data-request-service')
  declare restrictionsDataRequestService: Services['restrictions/data-request-service'];

  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  /* Task that can be used for Accepting/dismissing/snoozing/pushing of recommendations
  This will create respective recommendationAction objects in the BE (https://test.mylighthouse.com/admin/recommendations/recommendationaction/)
   */
  createRecommendationActionTask = task(
    { restartable: true },
    async (
      recommendationAction: Carecommendation['action'],
      recommendationObjects: (Carecommendation | CarecommendationMock)[],
      priceType: RecommendationActionPriceTypesType | null = null, // if no priceType is passed, the ota_price scenario is seen as default on the BE side
      override: PricingOverridePayload | null = null,
    ) => {
      const hotelId = this.hotelAndMarketService.hotel.id!;

      try {
        const requestUrl = formatUrl(
          '/apigateway/v1/app/pricing-assistant/recommendations/action/',
          {
            subscription_id: hotelId,
          },
          this.ajax.host,
        );

        const requestData: ActionConfigs = {
          action_configs: [],
        };

        if (override) {
          requestData.override = override;
        }

        recommendationObjects.forEach((recommendationObject) => {
          const actionConfig: ActionConfig = {
            subscription: parseInt(hotelId, 10),
            recommendation_id: recommendationObject.id,
            // For PMS price type, use pmsValue if available, otherwise fallback to value
            // This handles cases where pmsValue is undefined (e.g., days without recommendations)
            current_rate:
              priceType === RECOMMENDATION_ACTION_PRICE_TYPES.PMS_PRICE
                ? (recommendationObject.carate?.pmsValue ??
                  recommendationObject.carate?.value)
                : recommendationObject.carate?.value,
            from_date: recommendationObject.fromDate.format('YYYY-MM-DD'),
            recommended_value:
              priceType === RECOMMENDATION_ACTION_PRICE_TYPES.PMS_PRICE
                ? recommendationObject.recommendedPmsValue
                : recommendationObject.recommendedValue,
            overwritten_value: recommendationObject.overwrittenValue,
            factors: recommendationObject.factors,
            action: recommendationAction,
            carate: recommendationObject.carate?.id,
            pms_roomtype: recommendationObject.carate?.pmsRoomtype,
            rate_code: recommendationObject.carate?.rateCode,
          };

          if (priceType) {
            actionConfig['price_type'] = priceType;
          }

          requestData.action_configs.push(actionConfig);
        });

        const response = await this.ajax.post<
          CarecommendationActionEntry | BulkPushResponse
        >(requestUrl, {
          data: requestData,
          contentType: 'application/json',
        });

        const isBulkPush = 'action_configs' in response;
        const responseArray = isBulkPush ? response.action_configs : [response];
        const meta = response.meta ?? null;

        return {
          isSuccess: true,
          isBulkPush,
          meta,
          response: responseArray.map((responseItem) => ({
            recommendationObject: responseItem,
            action: responseItem.action,
            recommendationActionId: responseItem.id,
          })),
        };
      } catch (error: unknown) {
        // Check if the API returned a specific error message
        if (isApiError(error)) {
          this.errorTracking.addBreadcrumb({
            category: 'error',
            data: {
              detail: error.payload.errors[0].detail,
              title: error.payload.errors[0].title,
            },
          });

          // Throwing an exception with the error code to make the filtering of issues by error code in Sentry possible
          this.errorTracking.captureException(
            new Error(
              `CA createRecommendationAction request exception: ${error.payload.errors[0].code}`,
            ),
          );

          // Show a custom alert message when user tries to push a day that is already being pushed
          if (
            error.payload.errors[0].code ===
            RECOMMENDATIONS_ACTION_ENDPOINT_ERROR_CODES.DUPLICATE_RECOMMENDATION_ACTION
          ) {
            void this.alerting.showErrorAlert({
              title: this.intl.t(
                'pricing_assistant.duplicate_recommendation_action.error.title',
              ),
              text: this.intl.t(
                'pricing_assistant.duplicate_recommendation_action.error.text',
              ),
            });
            // Show a custom alert message when user pushed price would lead to negative pms rate
          } else if (
            error.payload.errors[0].code ===
            RECOMMENDATIONS_ACTION_ENDPOINT_ERROR_CODES.NEGATIVE_RATE_VALUE
          ) {
            void this.alerting.showErrorAlert({
              title: this.intl.t(
                'pricing_assistant.negative_rate_value.error.title',
              ),
              text: this.intl.t(
                'pricing_assistant.negative_rate_value.error.text',
              ),
            });
          } else {
            void this.alerting.showErrorAlert();
          }
        }

        return {
          action: recommendationAction,
          isSuccess: false,
          error,
        };
      }
    },
  );

  deleteOverridesTask = task(
    { restartable: true },
    async (overrideIds: string[]) => {
      try {
        const requestUrl = formatUrl(
          '/apigateway/v1/app/pricing-assistant/recommendations/overrides/',
          {
            subscription_id: this.hotelAndMarketService.hotel.id!,
            overrides: overrideIds.join(','),
          },
          this.ajax.host,
        );

        await this.ajax.delete(requestUrl);

        // Mirror destroyRecord: drop the deleted overrides from the store so the re-request starts clean
        overrideIds.forEach((id) => {
          const record = this.store.peekRecord<Override>('override', id);
          if (record) {
            this.store.unloadRecord(record);
          }
        });

        return {
          isSuccess: true,
        };
      } catch (error) {
        if (error) {
          this.errorTracking.captureException(error);
        }

        return {
          isSuccess: false,
          error,
        };
      }
    },
  );
  launchOverrideSimulateRequestTask = task(
    { restartable: true },
    async (hotelId: string, override: PricingOverridePayload) => {
      let apiResponse: { carecommendations: RawRecommendationData[] };

      try {
        const standardParams = {
          subscription_id: hotelId,
        };

        const requestUrl = formatUrl(
          '/apigateway/v1/app/pricing-assistant/recommendations/simulate/override',
          standardParams,
          this.ajax.host,
        );

        apiResponse = await this.ajax.post(requestUrl, {
          data: {
            override,
          },
          contentType: 'application/json',
        });

        return apiResponse.carecommendations;
      } catch (error) {
        this.basicErrorHandling(error);
      }

      return null; // in this case something went wrong with the request
    },
  );

  pricingAssistantCompetitorRatesRequestTask = task(
    async (
      hotelId: string,
      dateRangeStart: string,
      dateRangeEnd: string,
      compareDate: string,
      cemaCategory: OpenRoomPricingFixedCategory,
      params: Nullable<HotelPricingManagerParams>,
    ) => {
      const queryParams = {
        ...params,
        compareValue: compareDate,
        compareType: COMPARETYPES.DATE,
      };

      const apiParams = mapQueryParamsToAPIParams(
        queryParams,
        dateRangeStart,
        dateRangeEnd,
      );

      try {
        const apiResponse = await this.store.query<Period>('period', {
          namespace: APIGATEWAY,
          typeKey: 'rates',
          hotel_id: hotelId,
          agg_cema_category: cemaCategory,
          ...apiParams,
        });

        return [...apiResponse];
      } catch (error) {
        this.basicErrorHandling(error);
      }

      return null; // in this case something went wrong with the request
    },
  );

  // Pricing Assistant data & recommendation related tasks
  pricingAssistantGeneralDataRequestsTask = task(
    { restartable: true },
    async (
      hotelId: string,
      rangeStart: string,
      rangeEnd: string,
      month: Dayjs,
    ): Promise<void> => {
      const requests: Promise<unknown>[] = [];

      if (this.access.hasFeature('app.events')) {
        requests.push(
          this.store.query<Day>('day', {
            namespace: APIGATEWAY,
            typeKey: 'holidays',
            from_date_range_start: rangeStart,
            from_date_range_end: rangeEnd,
            hotel_id: hotelId,
          }),
          this.store.query<Day>('day', {
            namespace: APIGATEWAY,
            typeKey: 'events/accepted',
            from_date_range_start: rangeStart,
            from_date_range_end: rangeEnd,
            hotel_id: hotelId,
            include_distance: true,
          }),
        );
      }

      if (this.access.hasFeature('pms.otb.view')) {
        const queryParams: Record<string, string> = {
          namespace: APIGATEWAY,
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          subscription_id: hotelId,
        };

        // compare_extract_date, compare2_extract_date, etc.
        this.getChangeDates(month).forEach((changeDate, index) => {
          queryParams[`compare${index > 0 ? index + 1 : ''}_extract_date`] =
            changeDate;
        });

        requests.push(
          this.store.query<Occupancy>('occupancy', {
            ...queryParams,
            typeKey: 'revenueinsight/occupancies',
          }),
        );

        // Request for predicted OTB. There's no separate feature flag at the moment, so we have this request under the regular OTB feature flag
        requests.push(
          this.store.query<PredictedOccupancy>('predicted-occupancy', {
            ...queryParams,
            typeKey: 'revenueinsight/occupancies/predicted',
          }),
        );
      }

      try {
        await Promise.all(requests);
      } catch (error) {
        this.basicErrorHandling(error);
      }
    },
  );
  /**
   * Task that launches a CA Rate & recommendation request for a single day (using store.query)
   * @param {recommendation} hotelId : hotel id, needed for the requests
   * @param {String} fromDateString : from date we want refreshed, as a String in the YYYY-MM-DD format
   * @param {String} compareDate : compare date we want refreshed, as a String in the YYYY-MM-DD format
   */
  pricingAssistantRatesAndRecommendationsSingleDayRequestTask = task(
    {},
    async (hotelId: string, fromDateString: string, compareDate: string) => {
      const requests = [
        this.store.query<Carecommendation>('carecommendation', {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/recommendations',
          from_date_range_start: fromDateString,
          from_date_range_end: fromDateString,
          hotel_id: hotelId,
        }),
        this.store.query<Period>('period', {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/rates',
          from_date_range_start: fromDateString,
          from_date_range_end: fromDateString,
          hotel_id: hotelId,
          compare_extract_date: compareDate,
        }),
      ] as const;

      try {
        return Promise.all(requests);
      } catch (error) {
        void this.alerting.showErrorAlert();

        this.errorTracking.addBreadcrumb({
          message: 'Pricing Assistant post pushed action refresh failed',
        });
        this.errorTracking.captureException(error);
      }

      return null;
    },
  );

  pricingAssistantRatesRequestTask = task(
    async (
      hotelId: string,
      rangeStart: string,
      rangeEnd: string,
      compareDate: string,
    ) => {
      let apiResponse = null;

      try {
        apiResponse = await this.store.query<Period>('period', {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/rates',
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          hotel_id: hotelId,
          compare_extract_date: compareDate,
        });

        return [...apiResponse];
      } catch (error) {
        this.basicErrorHandling(error);
      }

      return null; // in this case something went wrong with the request
    },
  );

  pricingAssistantRecommendationReasonsRequestTask = task(
    {},
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      try {
        const apiResponse = await this.store.query<PaRecommendationReason>(
          'pa-recommendation-reason',
          {
            namespace: APIGATEWAY,
            typeKey: 'pricing-assistant/recommendations/reasons',
            from_date_range_start: rangeStart,
            from_date_range_end: rangeEnd,
            hotel_id: hotelId,
          },
        );

        return [...apiResponse];
      } catch (error) {
        void this.alerting.showErrorAlert();

        this.errorTracking.addBreadcrumb({
          message: 'Pricing Assistant recommendation reasons request failed',
        });

        this.errorTracking.captureException(error);
      }

      return null;
    },
  );

  pricingAssistantRecommendationsRequestTask = task(
    async (
      hotelId: string,
      rangeStartForRecommendationsRequest: string,
      rangeEndForRecommendationsRequest: string,
      restartRecommendationPollers = false,
    ) => {
      let apiResponse = null;

      try {
        apiResponse = [
          ...(await this.store.query<Carecommendation>('carecommendation', {
            namespace: APIGATEWAY,
            typeKey: 'pricing-assistant/recommendations',
            from_date_range_start: rangeStartForRecommendationsRequest,
            from_date_range_end: rangeEndForRecommendationsRequest,
            hotel_id: hotelId,
          })),
        ];

        // Note: Currently we have restart pollers initiation in the data request task which is not very ideal. The task should be only responsible for making the request.
        // However moving this logic to model hook instead and awaiting the response in there causes buggy page mode switch behaviour.
        // PRICING ASSISTANT TODO: Look into moving this logic somewhere more appropriate
        // Only restart polling if PMS is integrated
        if (
          restartRecommendationPollers &&
          this.pricingAssistantPmsSs.isPricingAssistantPMSIntegrated
        ) {
          // Restart polling for accepted and pushing
          this.pricingAssistantRecommendationActionPoller.restartPricingAssistantRecommendationActionIdPollers(
            apiResponse,
          );
        }

        return apiResponse;
      } catch (error) {
        this.basicErrorHandling(error);
      }

      return null; // in this case something went wrong with the request
    },
  );

  pricingAssistantRoomTypeRecommendationsTask = task(
    {},
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      try {
        const apiResponse = await this.store.query<RoomTypeRecommendation>(
          'room-type-recommendation',
          {
            namespace: APIGATEWAY,
            typeKey: 'pricing-assistant/recommendations/all_rooms',
            from_date_range_start: rangeStart,
            from_date_range_end: rangeEnd,
            hotel_id: hotelId,
          },
        );

        return apiResponse;
      } catch (error) {
        void this.alerting.showErrorAlert();

        this.errorTracking.addBreadcrumb({
          message: 'Pricing Assistant roomtype recommendations request failed',
        });

        this.errorTracking.captureException(error);
      }

      return null;
    },
  );

  recommendationsStrategiesTask = task(
    { restartable: true },
    async (settingsType?: RecommendationStrategy['settingsType']) => {
      let apiResponse = null;

      try {
        apiResponse = await this.store.query<RecommendationStrategy>(
          'recommendation-strategy',
          {
            namespace: APIGATEWAY,
            typeKey: 'pricing-assistant/recommendations/strategies',
            subscription_id: this.hotelAndMarketService.hotel.id,
            settings_types: settingsType ?? [],
          },
        );

        return [...apiResponse];
      } catch (error) {
        this.basicErrorHandling(error);
      }

      return null; // in this case something went wrong with the request
    },
  );

  requestOverridesTask = task(
    {},
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      try {
        const apiResponse = await this.store.query<Override>('override', {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/recommendations/overrides',
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          subscription_id: hotelId,
        });

        return apiResponse;
      } catch (error) {
        await this.alerting.showErrorAlert();

        this.errorTracking.addBreadcrumb({
          message: 'Pricing Assistant override request failed',
        });

        this.errorTracking.captureException(error);
      }

      return null;
    },
  );

  // Get a list of possible PMSs. Used in PA upsell at the moment.
  requestPmsTask = task({ restartable: true }, async () => {
    try {
      await this.store.query<Pms>('pms', {
        namespace: APIGATEWAY,
        typeKey: 'pricing-assistant/pms/versions',
      });
    } catch (error) {
      this.basicErrorHandling(error);
    }

    return null; // in this case something went wrong with the request
  });

  requestPricingAssistantPmsInfoTask = task(
    { restartable: true },
    async (hotelId: string) => {
      try {
        const apiResponse = await this.store.queryRecord<PaPmsInfo>(
          'pa-pms-info',
          {
            subscription_id: hotelId,
            namespace: APIGATEWAY,
            typeKey: 'pricing-assistant/pms/info',
          },
        );

        return apiResponse;
      } catch (error) {
        this.basicErrorHandling(error);
      }

      return null; // in this case something went wrong with the request
    },
  );

  requestPricingAssistantSubscriptionMetaTask = task(
    { restartable: true },
    async (hotelId: string) => {
      try {
        const apiResponse =
          await this.store.queryRecord<PaSubscriptionMetaInfo>(
            'pa-subscription-meta-info',
            {
              subscription_id: hotelId,
              namespace: APIGATEWAY,
              typeKey: 'pricing-assistant/subscription/meta/info',
            },
          );

        return apiResponse;
      } catch (error) {
        this.basicErrorHandling(error);
      }

      return null; // in this case something went wrong with the request
    },
  );

  requestRestrictionActionsOnPageLoadTask = task(
    { restartable: true },
    async (
      hotelId: string,
      fromDate: string,
      toDate: string,
    ): Promise<void> => {
      const restrictionActions =
        await this.restrictionsDataRequestService.restrictionActionRequestTask.perform(
          hotelId,
          fromDate,
          toDate,
        );

      if (macroCondition(!isTesting())) {
        this.restrictionsActionPoller.addActionsToFollow(restrictionActions);
      }
    },
  );
  reRequestDataAfterPushTask = task(
    {},
    async ({
      hotelId,
      fromDate,
      endDate,
      fetchOverrides = false,
    }: ReRequestDataAfterPushParams) => {
      type RequestsArray = [
        Promise<Carecommendation[]>,
        Promise<Period[]>,
        Promise<Override[]>?,
      ];

      const compareDate = this.hotelAndMarketService
        .getLocalTime()
        .subtract(PA_REF_DAYS_AGO, 'day')
        .format('YYYY-MM-DD');

      const requests: RequestsArray = [
        this.store.query<Carecommendation>('carecommendation', {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/recommendations',
          from_date_range_start: fromDate,
          from_date_range_end: endDate,
          hotel_id: hotelId,
        }),
        this.store.query<Period>('period', {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/rates',
          from_date_range_start: fromDate,
          from_date_range_end: endDate,
          hotel_id: hotelId,
          compare_extract_date: compareDate,
        }),
      ];

      if (fetchOverrides) {
        requests.push(
          this.store.query<Override>('override', {
            namespace: APIGATEWAY,
            typeKey: 'pricing-assistant/recommendations/overrides',
            from_date_range_start: fromDate,
            from_date_range_end: endDate,
            subscription_id: hotelId,
          }),
        );
      }

      try {
        const [carecommendation, carate, override] = await Promise.all(
          requests as Promise<unknown>[],
        );

        return {
          carecommendation: carecommendation as Carecommendation[],
          carate: carate as Period[],
          override: override ? (override as Override[]) : undefined,
        };
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.addBreadcrumb({
          message:
            'Pricing Assistant Recommendation action poller post-pushing refresh failed',
        });
        this.errorTracking.captureException(error);
      }

      return null;
    },
  );

  restrictionsRecommendationsRequestTask = task(
    { restartable: true },
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      if (!this.access.hasOTB) {
        return [];
      }

      type RestrictionsRecommendationsRequestParams = {
        from_date_range_end: string;
        from_date_range_start: string;
        hotel_id: string;
        namespace: typeof APIGATEWAY;
        typeKey: string;
      };

      let apiResponse = null;

      try {
        const requestParams: RestrictionsRecommendationsRequestParams = {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/restrictions/recommendations',
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          hotel_id: hotelId,
        };

        const response = await this.store.query<RestrictionRecommendation>(
          'restriction-recommendation',
          requestParams,
        );
        apiResponse = [...response];
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }

      return apiResponse;
    },
  );

  restrictionsRequestTask = task(
    { restartable: true },
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      let apiResponse = null;

      type RestrictionsRequestParams = {
        from_date_range_end: string;
        from_date_range_start: string;
        hotel_id: string;
        namespace: typeof APIGATEWAY;
        typeKey: string;
      };

      try {
        const requestParams: RestrictionsRequestParams = {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/restrictions/aggregated',
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          hotel_id: hotelId,
        };

        const response = await this.store.query<Period>(
          'period',
          requestParams,
        );
        apiResponse = [...response];
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }

      return apiResponse;
    },
  );

  roomOffsetHierarchyTask = task({ restartable: true }, async () => {
    if (!this.access.hasPricingAssistantDynamicRatePricingFeature) {
      return [];
    }

    try {
      const result = await this.recommendationsStrategiesTask.perform(
        'room_offset_hierarchy',
      );

      const strategy = result?.[0];
      const settings = strategy?.settings as
        | RoomOffsetHierarchySettings
        | undefined;

      const { hierarchy = {} } = settings ?? {};
      const flattenedHierarchy = getFlattenedHierarchy(hierarchy);

      return flattenedHierarchy;
    } catch (error) {
      this.basicErrorHandling(error);
      return [];
    }
  });

  saveAndUpdateAutopilotEnabledStateForADayTask = task(
    { restartable: true },
    async (fromDate: string, enableAutopilot: boolean) => {
      const hotelId = this.hotelAndMarketService.hotel.id!;
      let apiResponse: AutopilotExcludeResponse | null = null;

      try {
        const requestUrl = formatUrl(
          '/apigateway/v1/app/pricing-assistant/autopilot/exclude',
          { subscription_id: hotelId },
          this.ajax.host,
        );

        const data = { from_date: fromDate };

        if (enableAutopilot) {
          apiResponse = await this.ajax.delete<AutopilotExcludeResponse>(
            requestUrl,
            { data, contentType: 'application/json' },
          );
        } else {
          apiResponse = await this.ajax.put<AutopilotExcludeResponse>(
            requestUrl,
            { data, contentType: 'application/json' },
          );
        }

        // Update paAutopilotSettings under the hotel resource so that the change is reflected under the Autopilot settings page
        set(
          this.hotelAndMarketService.hotel,
          'paAutopilotSettings',
          apiResponse.pa_autopilot_settings,
        );

        return {
          isSuccess: true,
          apiResponse,
        };
      } catch (error) {
        this.basicErrorHandling(error);

        return {
          isSuccess: false,
          error,
        };
      }
    },
  );

  // Task that can be used for Accepting/dismissing/snoozing/pushing of recommendations
  trackReturnedRecommendationsTask = task(
    { restartable: true },
    async (apiResponse: Carecommendation[]) => {
      // We don't want the 5s timeout in every Pricing Manager visual test scenario where the tracking task is performed
      if (macroCondition(isTesting())) {
        return;
      }

      try {
        // Note: wait for 5 seconds before actually launching the Segment request
        // We want to avoid logging recommendations when users are just switching between months using the datepicker
        await timeout(5000);

        const recommendationDataToTrack = apiResponse.map(
          (recommendationRecord) => {
            const serializedCarate = recommendationRecord.carate?.serialize({
              includeId: true,
            });
            const serializedRecommendation = recommendationRecord.serialize({
              includeId: true,
            });

            // Note: no typing added here as it's probably not very useful
            // We'd need to add interfaces for the serialized models and those might get outdated over time
            const endResult: Record<string, unknown> = {
              ...(serializedRecommendation as Record<string, unknown>),
              rate_config: serializedCarate, // Dump all serialized carate data under the rate_config key
            };

            return endResult;
          },
        );

        this.usageAnalytics.track('Recommendations viewed', {
          eventTrigger: 'recommendations-calendar',
          subscription_id: this.hotelAndMarketService.hotel.id,
          recommendations: recommendationDataToTrack,
          product: 'pricing-manager',
        });
      } catch (error: unknown) {
        this.errorTracking.addBreadcrumb({
          message:
            'Pricing Assistant recommendation Segment tracking attempt failed',
        });
        this.errorTracking.captureException(error);
      }
    },
  );

  basicErrorHandling(error: unknown): void {
    if (error) {
      this.errorTracking.captureException(error);
    }

    void this.alerting.showErrorAlert({
      text: getAPIErrorMessage(error, this.intl),
    });
  }

  // Duplicate with rates route for now
  getChangeDates(month: Dayjs): string[] {
    const today = this.hotelAndMarketService.getLocalTime();
    const previousMonth = today.subtract(1, 'month');
    const nextMonth = today.add(1, 'month');
    const monthAfterNextMonth = today.add(2, 'month');

    if (month.isSame(today, 'month') || month.isSame(previousMonth, 'month')) {
      return [today.subtract(1, 'day').format('YYYY-MM-DD')];
    }
    if (
      month.isSame(nextMonth, 'month') ||
      month.isSame(monthAfterNextMonth, 'month')
    ) {
      return [
        today.subtract(1, 'day').format('YYYY-MM-DD'),
        today.subtract(7, 'day').format('YYYY-MM-DD'),
      ];
    }
    if (month.isAfter(monthAfterNextMonth)) {
      return [today.subtract(7, 'day').format('YYYY-MM-DD')];
    }

    return [];
  }
}

declare module '@ember/service' {
  interface Registry {
    'pricing-assistant-data-request-service': PricingAssistantDataRequestServiceService;
  }
}
